import { useEffect, useMemo, useState } from 'react';
import { fetchGraph } from '../../../services/graphService';
import { IntelligenceUnavailableError, fetchNetwork } from '../intelligenceApi';
import type { NetworkAnalysis } from '../graphAnalytics';
import { DEFAULT_FILTERS, analyzeView, filterGraph, fromLegacyGraph } from '../network/networkModel';
import type { NetworkGraph } from '../types';
import { egoView, type ProfileNetworkView } from './egoNetwork';

/**
 * The network, shared by every profile in the session.
 *
 * One entity's corner of it is read from the whole thing — the same numbers
 * the Network workspace shows — so it is fetched and analysed once and held
 * for a while rather than being rebuilt each time somebody opens a profile.
 */
const TTL = 5 * 60 * 1000;

let cached: { at: number; graph: NetworkGraph } | null = null;
let inflight: Promise<NetworkGraph> | null = null;
let analysed: { graph: NetworkGraph; analysis: NetworkAnalysis } | null = null;

/** For tests, and for after something has changed the network */
export function clearProfileNetworkCache(): void {
  cached = null;
  inflight = null;
  analysed = null;
}

async function loadNetwork(): Promise<NetworkGraph> {
  if (cached && Date.now() - cached.at < TTL) return cached.graph;
  if (!inflight) {
    inflight = (async () => {
      try {
        return await fetchNetwork({ includePast: true });
      } catch (error) {
        if (!(error instanceof IntelligenceUnavailableError)) throw error;
        return fromLegacyGraph(await fetchGraph({ currentOnly: false }));
      }
    })()
      .then((graph) => {
        cached = { at: Date.now(), graph };
        return graph;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Percentiles and cut points, computed once per network */
function analysisOf(graph: NetworkGraph): NetworkAnalysis {
  if (analysed?.graph === graph) return analysed.analysis;
  // The ties that hold, which is what "well connected" has to mean
  const view = filterGraph(graph, DEFAULT_FILTERS);
  const analysis = analyzeView(view.nodes, view.links);
  analysed = { graph, analysis };
  return analysis;
}

export interface ProfileNetworkState extends ProfileNetworkView {
  loading: boolean;
  /** The network could not be read; the profile simply leaves the section out */
  failed: boolean;
}

export function useProfileNetwork(id: string | undefined): ProfileNetworkState {
  const [graph, setGraph] = useState<NetworkGraph | null>(cached?.graph ?? null);
  const [loading, setLoading] = useState(!cached);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setFailed(false);
    if (!cached) setLoading(true);
    loadNetwork()
      .then((next) => {
        if (!cancelled) setGraph(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const analysis = useMemo(() => (graph ? analysisOf(graph) : null), [graph]);
  const view = useMemo(() => egoView(graph, analysis, id), [graph, analysis, id]);

  return { ...view, loading, failed };
}
